import styled from 'styled-components'
import _ from 'lodash'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useAsyncEffect } from '../../common/src/hooks/useAsyncEffect'
import { useDispatch } from 'react-redux'
import { DetailsList, Image, Link, ProgressIndicator, Selection, SelectionMode } from '@fluentui/react'
import { mapValuesToJsArray } from '../../common/src/helpers/immutableHelpers'
import { getFileExtensionIconURL } from '../../common/src/constants'
import { ICON_NAMES } from '../../common/src/msIcons'
import { AlertModal } from '../../components/modal/AlertModal'
import { fileHelper, itemHelper } from '../../common/src/helpers'
import { externalLinkHandler } from '../../utils/externalLinkHandler'
import { FileContext } from '../../contexts'
import { useMergeState } from '../../common/src/hooks/enhancedHooks'
import { useCopyColumn } from '../../components/item/useCopyColumn'
import { useTranslation } from 'react-i18next'
import { toDate } from '../../common/src/helpers/dateHelper'
import { AttributePlaceholder } from '../../components/placeholder/AttributePlaceholder'
import { rowStyles, Placeholder, ResourcesSectionCard, SectionHeader, SectionTitle } from './resourcesSectionCommon'
import { getOneDriveFolderForItem } from '../../common/src/actions/itemsAPI'
import { Popover, PopoverSurface, PopoverTrigger, Toolbar, ToolbarButton } from '@fluentui/react-components'
import { Tooltip } from '@/components/tooltip/Tooltip'
import { ArrowDownload, ArrowUpload, Delete, OpenFolder } from '../../components/BundledIcons'
import { useDownloadFiles } from '../../hooks/filesHooks'
import { isElectronApp } from '../../integrations/electron'
import { formatDate } from '../../utils/datefns'

const _style = {
  fileIconHeaderIcon: {
    padding: 0,
    fontSize: '16px',
  },
  fileIconCell: {
    textAlign: 'center',
  },
  fileNameHeaderIcon: {
    padding: 0,
    fontSize: '16px',
  },
  fileNameCell: {
    textAlign: 'center',
  },
}

const StyledLink = styled(Link).attrs({
  target: '_blank',
  rel: 'noopener noreferrer',
  styles: {
    root: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      '.is-selected &': {
        fontWeight: 600,
      },
    },
  },
})``

const FileIcon = styled(Image)`
  vertical-align: middle;
  max-height: 16px;
  max-width: 16px;
`

const DateText = styled(AttributePlaceholder)`
  font-size: 11px;
  font-weight: lighter;
  color: ${p => p.theme.palette.neutralDark};
  height: auto;
  margin: 4px 0 0;
`

const getDeleteConfirmationText = t => (filename, count) => {
  let message
  if (count === 1) {
    message = t('item.file.want_delete_file', { filename })
  } else {
    message = t('item.file.want_delete_count', { count })
  }
  return `${message} ${t('item.file.deleted_warning')}`
}

const timestampToFormattedDate = timestamp => {
  const date = toDate(timestamp)
  const text = date ? formatDate('ShortenedNormal')(date) : ''
  return _.replace(text, 'de ', '') // shorten text if Spanish
}

export const DateForResource = ({ timestamp }) => {
  const formattedDate = timestampToFormattedDate(timestamp)
  const { t } = useTranslation()
  return <DateText name={formattedDate} title={t('item.file.date_tooltip', { date: formattedDate })} />
}

export const useOneDriveFolder = ({ itemID, files }) => {
  const dispatch = useDispatch()
  const [oneDriveFolder, setOneDriveFolder] = useState('')
  useAsyncEffect(
    async getIsMounted => {
      setOneDriveFolder('')
      const file = files.find(fileHelper.isOneDriveStorage)
      if (!file) return
      const oneDriveData = await dispatch(getOneDriveFolderForItem(itemID))
      if (!getIsMounted()) {
        return
      }
      const link = oneDriveData?.payload?.folder_link
      setOneDriveFolder(link)
    },
    [itemID, files]
  )
  return oneDriveFolder
}

export const FilesSectionContent = ({ onAddFile, item, readOnly = false }) => {
  const [state, setState] = useMergeState({
    loading: false,
    selectedCount: 0,
    selection: new Selection({
      onSelectionChanged: () => {
        setState({ selectedCount: state.selection.getSelectedCount() })
      },
    }),
    showAlertModalToDelete: false,
  })
  const files = itemHelper.getFiles(item)
  const itemID = itemHelper.getId(item)
  const { t } = useTranslation()
  const copyColumn = useCopyColumn()
  const oneDriveFolderLink = useOneDriveFolder({ itemID, files })
  const { downloadFiles } = useDownloadFiles()

  const { loading, selectedCount, selection, showAlertModalToDelete } = state
  const { deleteFiles } = useContext(FileContext)
  const filesItems = useMemo(() => {
    return mapValuesToJsArray(files, f => {
      return {
        filename: f.get('filename'),
        url: f.get('url'),
        itemFile: f,
        timestamp: f.get('timestamp'),
      }
    })
  }, [files])

  useEffect(() => {
    selection.setItems(filesItems, true)
  }, [filesItems, selection])

  // Show automatically panel to upload a file
  /*useEffect(() => {
    if (filesItems.length === 0 && !readOnly) {
      inputRef.current.click()
    }
  }, [])*/

  const downloadSelectedFiles = useCallback(() => {
    if (!selectedCount) {
      return
    }
    const selectedItems = selection.getSelection()
    const itemFiles = selectedItems.map(item => item.itemFile)
    return downloadFiles(itemFiles)
  }, [selectedCount, selection, downloadFiles])

  const deleteSelectedFiles = useCallback(async () => {
    if (!selectedCount) {
      return
    }
    const selectedItems = selection.getSelection()
    const itemFiles = _.map(selectedItems, item => item.itemFile)

    setState({
      showAlertModalToDelete: false,
      loading: true,
    })
    await deleteFiles({ itemFiles })
    setState({ loading: false })
  }, [deleteFiles, selectedCount, selection, setState])

  const wantToDeleteSelectedFiles = useCallback(() => {
    setState({ showAlertModalToDelete: true })
  }, [setState])

  const alertModalSubText = useMemo(() => {
    if (!selectedCount) {
      return null
    }
    const selectedItems = selection.getSelection()
    const firstSelectedItem = _.first(selectedItems)
    const { itemFile } = firstSelectedItem
    const filename = fileHelper.getFilename(itemFile)
    return getDeleteConfirmationText(t)(filename, selectedCount)
  }, [selectedCount, selection, t])

  const columns = [
    {
      key: 'column1',
      name: t('item.file.type_column'),
      className: _style.fileIconCell,
      iconClassName: _style.fileIconHeaderIcon,
      iconName: ICON_NAMES.Page,
      isIconOnly: true,
      fieldName: 'name',
      minWidth: 16,
      maxWidth: 16,
      onRender: item => {
        const src = getFileExtensionIconURL(item.filename)
        const onClick = evt => {
          const handled = externalLinkHandler(item.url)
          if (handled) {
            evt.preventDefault()
          }
        }
        return (
          <StyledLink href={item.url} onClick={onClick}>
            <FileIcon height={16} width={16} src={src} />
          </StyledLink>
        )
      },
    },
    {
      key: 'column2',
      name: t('item.file.name_column'),
      className: _style.fileNameCell,
      isIconOnly: false,
      minWidth: 16,
      onColumnClick: null,
      onRender: simpleFile => {
        const fileID = fileHelper.getID(simpleFile.itemFile)
        return <FilenameCell simpleFile={simpleFile} key={fileID} id={fileID} />
      },
    },
    copyColumn,
  ]

  const placeholder = !readOnly && <Placeholder>{t('item.file.placeholder')}</Placeholder>

  const deleteButton = (
    <Tooltip content={t('item.file.delete_files')} relationship="label">
      <ToolbarButton
        icon={<Delete />}
        onClick={selectedCount === 0 ? undefined : wantToDeleteSelectedFiles}
        disabled={loading}
      />
    </Tooltip>
  )
  const downloadButton = (
    <Tooltip content={t('item.file.download_files')} relationship="label">
      <ToolbarButton icon={<ArrowDownload />} onClick={selectedCount === 0 ? undefined : downloadSelectedFiles} />
    </Tooltip>
  )
  const showSelectPopover = selectedCount === 0
  const onRenderRow = (props, defaultRender) => {
    props.styles = rowStyles
    if (downloadFiles && isElectronApp()) {
      const itemFile = props?.item?.itemFile
      props.onClick = evt => {
        if (evt.target.tagName !== 'A') {
          return
        }
        evt.preventDefault?.()
        if (itemFile) {
          downloadFiles([itemFile])
        }
      }
    }

    return defaultRender(props)
  }

  return (
    <ResourcesSectionCard>
      <SectionHeader>
        <SectionTitle>{t('item_detail.files_section_title')}</SectionTitle>
        {!readOnly && (
          <Toolbar>
            {oneDriveFolderLink && (
              <Tooltip content={t('item_detail.open_folder_tooltip')} relationship="description">
                <ToolbarButton icon={<OpenFolder />} as="a" href={oneDriveFolderLink} target="_blank" />
              </Tooltip>
            )}
            <ToolbarButton disabled={loading} icon={<ArrowUpload />} onClick={onAddFile}>
              {t('item.file.upload')}
            </ToolbarButton>
            {showSelectPopover ? <FirstSelectPopover>{deleteButton}</FirstSelectPopover> : deleteButton}
            {showSelectPopover ? <FirstSelectPopover>{downloadButton}</FirstSelectPopover> : downloadButton}
          </Toolbar>
        )}
      </SectionHeader>
      {loading && <ProgressIndicator />}
      {files.size > 0 ? (
        <DetailsList
          setKey={'files'}
          items={filesItems}
          columns={columns}
          selectionMode={SelectionMode.multiple}
          selection={selection}
          onRenderRow={onRenderRow}
        />
      ) : (
        placeholder
      )}
      <AlertModal
        title={t('item.file.delete_confirmation_title')}
        subText={alertModalSubText}
        open={showAlertModalToDelete}
        onDismiss={() => {
          setState({ showAlertModalToDelete: false })
        }}
        primaryActionText={t('general.delete')}
        onPrimaryActionClick={deleteSelectedFiles}
      />
    </ResourcesSectionCard>
  )
}

const FilenameCell = ({ simpleFile, id }) => {
  const onClick = evt => {
    const handled = externalLinkHandler(simpleFile.url)
    if (handled) {
      evt.preventDefault()
    }
  }
  return (
    <>
      <span>
        <StyledLink href={simpleFile.url} onClick={onClick} title={simpleFile.filename}>
          {simpleFile.filename}
        </StyledLink>
      </span>
      <DateForResource timestamp={simpleFile.timestamp} />
    </>
  )
}

const FirstSelectPopover = ({ children }) => {
  const { t } = useTranslation()
  return (
    <Popover>
      <PopoverTrigger disableButtonEnhancement>{children}</PopoverTrigger>
      <PopoverSurface>{t('item.file.first_select')}</PopoverSurface>
    </Popover>
  )
}
